<template>
	<div class="transactions-table-quick-filters">
		<ArkListboxDropdown
			v-model="transactionStatuses"
			:options="transactionStatusSelectOptions"
			:multiple="true"
			:clearable="true"
			:searchable="true"
			:searchNormalizer="normalizeLabel"
			:same-width="false"
			class="status-filter"
		>
			<template #trigger="{ selectedCount }">
				<Button variant="tertiary" size="xs" shape="pill" :class="{ active: selectedCount > 0 }">
					{{ t("filters.transaction-status-short") }}
					<span v-if="selectedCount" class="count">{{ selectedCount }}</span>
				</Button>
			</template>
		</ArkListboxDropdown>

		<ArkListboxDropdown
			v-model="municipalities"
			:options="municipalitySelectOptions"
			:multiple="true"
			:clearable="true"
			:searchable="true"
			:searchNormalizer="normalizeLabel"
			:same-width="false"
		>
			<template #trigger="{ selectedCount }">
				<Button variant="tertiary" size="xs" shape="pill" :class="{ active: selectedCount > 0 }">
					{{ t("filters.municipalities-title") }}
					<span v-if="selectedCount" class="count">{{ selectedCount }}</span>
				</Button>
			</template>
		</ArkListboxDropdown>

		<Popover.Root v-model:open="dateOpen" :positioning="{ placement: 'bottom-start', gutter: 4 }">
			<Popover.Trigger as-child>
				<Button variant="tertiary" size="xs" shape="pill" :class="{ active: hasDateRange }">
					{{ t("filters.data-range") }}
				</Button>
			</Popover.Trigger>
			<Popover.Positioner>
				<Popover.Content class="popover">
					<ArkDateRangePicker
						:start-date="dateRange.min ?? undefined"
						:end-date="dateRange.max ?? undefined"
						:show-title="false"
						inline-custom-calendar
						@date-range-change="onDateRangeChange"
					/>
				</Popover.Content>
			</Popover.Positioner>
		</Popover.Root>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, toRaw, watch, nextTick } from "vue"
import { Popover } from "@ark-ui/vue"
import { useI18n } from "vue-i18n"
import { UseRootStore } from "@/model/RootStore"
import { TransactionMetadataStatus, type Municipality } from "@/model/DataModel"
import { useView } from "@/composables/useView"
import { ViewScope } from "@/types"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import ArkDateRangePicker from "@/components/ui/ArkDateRangePicker.vue"
import Button from "@/components/ui/Button.vue"
import { transactionMetadataStatusTranslationKeys } from "@/utils/filterTranslationKeys"

const { t } = useI18n()
const store = UseRootStore()
const view = useView(ViewScope.TransactionsTable)

const transactionStatuses = ref<string[]>([])
const municipalities = ref<string[]>([])
const dateRange = ref<{ min: Date | null; max: Date | null }>({ min: null, max: null })
const dateOpen = ref(false)
let isApplyingViewFilter = false

function onDateRangeChange(data: { start: Date | null; end: Date | null }) {
	dateRange.value = { min: data.start, max: data.end }
}

const hasDateRange = computed(() => dateRange.value.min != null || dateRange.value.max != null)

function resetFilters() {
	transactionStatuses.value = []
	municipalities.value = []
	dateRange.value = { min: null, max: null }
	view.setTransactionStatuses([])
	view.setTransactionMunicipalities([])
	view.setTransactionDateRange({ min: null, max: null })
}

function hasActiveFilters(): boolean {
	return (
		transactionStatuses.value.length > 0 ||
		municipalities.value.length > 0 ||
		dateRange.value.min != null ||
		dateRange.value.max != null
	)
}

defineExpose({ resetFilters, hasActiveFilters })

const transactionStatusSelectOptions = computed(() =>
	Object.values(TransactionMetadataStatus).map(value => ({
		value,
		label: t(transactionMetadataStatusTranslationKeys[value]),
	}))
)

const availableMunicipalityRecords = ref<Municipality[]>([])

const municipalitySelectOptions = computed(() =>
	[...new Set(toRaw(availableMunicipalityRecords.value).map(municipality => municipality.name))]
		.map(name => ({ value: name, label: name }))
		.sort((left, right) => left.label.localeCompare(right.label))
)

// Normalize a label by removing accents and lowercasing it
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
function normalizeLabel(name: string) {
	return name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
}

function dateValue(value: string | null | undefined): Date | null {
	if (!value) {
		return null
	}

	const date = new Date(`${value}T00:00:00.000`)
	return Number.isNaN(date.getTime()) ? null : date
}

function syncFilterValue<T>(source: { value: T }, update: (value: T) => void): void {
	watch(
		source,
		value => {
			if (isApplyingViewFilter) return
			update(value)
		},
		{ deep: true }
	)
}

syncFilterValue(transactionStatuses, view.setTransactionStatuses)
syncFilterValue(municipalities, view.setTransactionMunicipalities)
syncFilterValue(dateRange, view.setTransactionDateRange)

watch(
	() => view.view.value.filter?.transaction,
	filter => {
		isApplyingViewFilter = true
		transactionStatuses.value = filter?.statuses ?? []
		municipalities.value = filter?.municipalities ?? []
		dateRange.value = {
			min: dateValue(filter?.date?.min),
			max: dateValue(filter?.date?.max),
		}
		nextTick(() => {
			isApplyingViewFilter = false
		})
	},
	{ deep: true, immediate: true }
)

onMounted(async () => {
	availableMunicipalityRecords.value = await store.getMunicipalityRecords
})
</script>

<style scoped>
.transactions-table-quick-filters {
	display: flex;
	align-items: center;
	gap: var(--spacing-xs);
	--width-select-container: 200px;

	:deep(.btn) {
		gap: var(--spacing-xs);
	}
}

.status-filter {
	--width-select-container: 140px;
}

.count {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: var(--space-16);
	height: var(--space-16);
	padding: 0 var(--spacing-xxs);
	border-radius: var(--border-radius-full);
	background: var(--neutral-200);
	color: var(--neutral-900);
	font-size: var(--font-size-xxs);
	font-weight: var(--font-weight-semibold);
}

.active {
	border-color: var(--neutral-900);
}

.popover {
	z-index: 100;
	width: 480px;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	padding: var(--spacing-md) var(--spacing-sm) var(--spacing-sm);
	box-shadow: var(--shadow-lg);

	:deep(.date-presets) {
		margin-bottom: var(--spacing-xs);

		.preset-btn {
			padding: var(--spacing-xxs) var(--spacing-sm);
		}
	}

	:deep(.date-summary) {
		padding-top: var(--spacing-xs);
	}
}
</style>
